/**
 * NPEFixationListPage — danh sách NPE Fixations. Đồng bộ ContractListView.
 * Edit/Delete disable khi (allocations_count > 0).
 */
import { type MouseEvent, useMemo, useState } from 'react'
import {
  Alert, Badge, Box, Button, CircularProgress, Divider, Popover,
  Stack, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useNavigate } from 'react-router-dom'

import ConfirmDialog from '@/components/ConfirmDialog'
import RoleGuard from '@/components/RoleGuard'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useDeleteNPEFixationMutation, useGetNPEFixationQuery, useLazyPrintNPEFixationQuery, useListNPEFixationsQuery,
} from '@/store/api/npeFixationsApi'
import type { NPEFixationOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import NPEFixationFormDialog from './NPEFixationFormDialog'

export interface NPEFixationListPageProps {
  embeddedContractNo?: string
}

type FilterKind = 'search' | 'contractNo'
const ALL_ROWS_PAGE_SIZE = 1_000_000

const filterBarBtnSx: SxProps<Theme> = (theme) => ({
  minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, textTransform: 'none', fontSize: '0.7rem', lineHeight: 1.1,
  '&.MuiButton-outlined': { borderColor: alpha(theme.palette.text.primary, 0.14), '&:hover': { borderColor: alpha(theme.palette.text.primary, 0.24), backgroundColor: alpha(theme.palette.text.primary, 0.04) } },
})
const actionToolbarBtnSx: SxProps<Theme> = { minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, fontSize: '0.72rem', lineHeight: 1.1, textTransform: 'none' }

type ColumnKind = 'text' | 'date' | 'number' | 'badge'
type ColumnDef = { key: keyof NPEFixationOut | '_allocs'; label: string; kind?: ColumnKind; align?: 'right' | 'center'; digits?: number }

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'fixation_date', label: 'Ngày Fix', kind: 'date' },
  { key: 'fixation_no', label: 'Fixation No' },
  { key: 'contract_no', label: 'NPE Contract' },
  { key: 'p_contract_no', label: 'P Contract' },
  { key: 'supplier_name', label: 'Nhà cung cấp' },
  { key: 'quantity_kg', label: 'SL (kg)', kind: 'number', align: 'right', digits: 0 },
  { key: 'price', label: 'Giá fix', kind: 'number', align: 'right', digits: 2 },
  { key: '_allocs', label: 'Allocations', kind: 'badge', align: 'center' },
  { key: 'note', label: 'Ghi chú' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${`${d.getFullYear()}`.slice(-2)}` }
const formatNumberFixed = (v: number, digits = 0): string => digits > 0 ? v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : (Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))

const rowMatchesSearch = (row: NPEFixationOut, term: string): boolean => {
  const needle = term.trim().toLowerCase()
  if (!needle) return true
  const fields: Array<keyof NPEFixationOut> = ['fixation_no', 'contract_no', 'p_contract_no', 'supplier_name', 'note']
  return fields.some((k) => { const v = row[k]; return v != null && String(v).toLowerCase().includes(needle) })
}

const cellValueText = (row: NPEFixationOut, col: ColumnDef): string => {
  if (col.key === '_allocs') return String(row.allocations_count ?? 0)
  const val = row[col.key as keyof NPEFixationOut]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'number' && typeof val === 'number') return formatNumberFixed(val, col.digits ?? 0)
  if (typeof val === 'number') return formatNumberFixed(val, 0)
  return String(val)
}

export function NPEFixationListPage({ embeddedContractNo }: NPEFixationListPageProps = {}) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const navigate = useNavigate()

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const [searchTerm, setSearchTerm] = useState('')
  const [contractFilter, setContractFilter] = useState(embeddedContractNo ?? '')

  const [filterPopover, setFilterPopover] = useState<{ kind: FilterKind; anchor: HTMLElement } | null>(null)
  const closeFilterPopover = () => setFilterPopover(null)
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => setFilterPopover({ kind, anchor: e.currentTarget })

  const hasActiveFilters = Boolean(searchTerm.trim() || (!embeddedContractNo && contractFilter))
  const clearAllFilters = () => { setSearchTerm(''); if (!embeddedContractNo) setContractFilter(''); setPage(0) }

  const [createOpen, setCreateOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<NPEFixationOut | null>(null)

  const { data, isLoading, isFetching, error } = useListNPEFixationsQuery({ page: 1, page_size: ALL_ROWS_PAGE_SIZE })
  const { data: editingDetail } = useGetNPEFixationQuery(editingId ?? 0, { skip: !editingId })

  const [deleteFix, { isLoading: deleting }] = useDeleteNPEFixationMutation()
  const [triggerPrint] = useLazyPrintNPEFixationQuery()

  const baseRows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(() =>
    baseRows.filter((row) => {
      if (!rowMatchesSearch(row, searchTerm)) return false
      if (contractFilter && row.contract_no !== contractFilter) return false
      return true
    }), [baseRows, searchTerm, contractFilter])

  const rows = useMemo(() => rowsPerPage === -1 ? filteredRows : filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [filteredRows, page, rowsPerPage])

  const allocatedRows = useMemo(() => baseRows.filter((r) => (r.allocations_count ?? 0) > 0), [baseRows])

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`
  const selectedRow = useMemo(() => baseRows.find((r) => r.cs_fixation_id === selectedId) ?? null, [baseRows, selectedId])

  const handleViewLocalP = () => { if (!selectedRow?.p_contract_no) return; navigate(`/purchase/local-p/${encodeURIComponent(selectedRow.p_contract_no)}`) }
  const handleAllocate = () => { if (!selectedRow) return; navigate(`/purchase/npe-fixations/${selectedRow.cs_fixation_id}/allocate`) }
  const handleEdit = () => { if (selectedRow) setEditingId(selectedRow.cs_fixation_id) }
  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try { await deleteFix(confirmDelete.cs_fixation_id).unwrap(); snack.success(`Đã xoá Fixation ${confirmDelete.fixation_no}`); setConfirmDelete(null) }
    catch (err) { snack.error(getErrorMessage(err, 'Xoá fixation thất bại')) }
  }
  const handlePrint = async () => {
    if (!selectedRow) return
    try { await triggerPrint(selectedRow.cs_fixation_id).unwrap(); snack.info('Yêu cầu in đã gửi') }
    catch (err) { snack.error(getErrorMessage(err, 'In PDF thất bại')) }
  }

  const editDisabled = !selectedRow || (selectedRow?.allocations_count ?? 0) > 0

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>Filter</Typography>
          <Button size="small" variant={searchTerm.trim() ? 'contained' : 'outlined'} color={searchTerm.trim() ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('search', e)} sx={filterBarBtnSx}>Search</Button>
          {!embeddedContractNo && (
            <Button size="small" variant={contractFilter ? 'contained' : 'outlined'} color={contractFilter ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('contractNo', e)} sx={filterBarBtnSx}>NPE Contract</Button>
          )}
          {hasActiveFilters && <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>Clear All</Button>}
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => setCreateOpen(true)}>New</Button>
          <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!selectedRow} onClick={handleAllocate}>Allocate</Button>
          <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!selectedRow?.p_contract_no} onClick={handleViewLocalP}>View P-HĐ</Button>
          <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={editDisabled || !canEdit} onClick={handleEdit}>Edit</Button>
          <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={editDisabled || !canEdit} onClick={() => selectedRow && setConfirmDelete(selectedRow)}>Delete</Button>
          <Button variant="outlined" size="small" sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }} disabled={!selectedRow} onClick={() => void handlePrint()}>Print</Button>
        </Stack>
      </Stack>
      <Divider sx={{ mb: 0.5 }} />

      {error ? <Alert severity="error" sx={{ mb: 1 }}>Failed to load NPE Fixations.</Alert> : null}

      {allocatedRows.length > 0 && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 0.5, py: 0 }}>
          {allocatedRows.length} fixation(s) đã có allocations — không thể sửa/xoá.
        </Alert>
      )}

      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', minHeight: 30, py: 0.125 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Total: {filteredRows.length}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Loaded: {baseRows.length}</Typography>
            <TablePagination component="div" count={filteredRows.length} page={rowsPerPage === -1 ? 0 : page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]} labelRowsPerPage="Rows" sx={{ flexShrink: 0, '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': { fontSize: '0.72rem' } }} />
          </Stack>
        </Box>

        <Popover open={filterPopover !== null} anchorEl={filterPopover?.anchor ?? null} onClose={closeFilterPopover} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} PaperProps={{ sx: { p: 1.5, minWidth: 240, maxWidth: 360 } }}>
          {filterPopover?.kind === 'search' && (
            <Stack spacing={1}>
              <TextField size="small" fullWidth autoFocus label="Tìm kiếm" placeholder="Fixation no, NPE no, supplier..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0) }} />
              <Button size="small" variant="text" onClick={() => { setSearchTerm(''); setPage(0); closeFilterPopover() }}>Clear Search</Button>
            </Stack>
          )}
          {filterPopover?.kind === 'contractNo' && (
            <Stack spacing={1}>
              <TextField size="small" fullWidth autoFocus label="NPE Contract" placeholder="NPE-XXXXX" value={contractFilter} onChange={(e) => { setContractFilter(e.target.value); setPage(0) }} />
              <Button size="small" variant="text" onClick={() => { setContractFilter(''); setPage(0); closeFilterPopover() }}>Clear</Button>
            </Stack>
          )}
        </Popover>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col) => (
                  <TableCell key={String(col.key)} align={col.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}>{col.label}</TableCell>
                ))}
              </TableRow>
            )}
            itemContent={(_: number, row: NPEFixationOut) =>
              TABLE_COLUMNS.map((col) => {
                if (col.key === '_allocs') {
                  const c = row.allocations_count ?? 0
                  return (
                    <TableCell key={String(col.key)} align={col.align} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                      <Badge badgeContent={c} color={c > 0 ? 'warning' : 'default'} showZero><Box sx={{ minWidth: 16 }} /></Badge>
                    </TableCell>
                  )
                }
                const val = cellValueText(row, col)
                return (
                  <TableCell key={String(col.key)} align={col.align} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                    <Box component="span" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>{val || '-'}</Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 1700, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const id = Number.isFinite(idx) ? rows[idx]?.cs_fixation_id : null
                return <TableRow {...props} hover selected={id != null && id === selectedId} sx={{ cursor: 'pointer' }} onClick={() => { if (id != null) setSelectedId(id) }} />
              },
            }}
          />
        </Box>
      </Box>

      <NPEFixationFormDialog open={createOpen} mode="create" defaultNpeContractNo={embeddedContractNo} onClose={() => setCreateOpen(false)} />
      <NPEFixationFormDialog open={!!editingId && !!editingDetail?.data} mode="edit" initialValue={editingDetail?.data ?? null} onClose={() => setEditingId(null)} />
      <ConfirmDialog open={!!confirmDelete} title="Xoá Fixation" message={confirmDelete ? `Bạn chắc chắn muốn xoá Fixation ${confirmDelete.fixation_no}?` : ''} destructive loading={deleting} onConfirm={() => void handleConfirmDelete()} onCancel={() => setConfirmDelete(null)} />
    </Box>
  )
}

export default NPEFixationListPage
